import { BrowserRouter, Routes, Route, Outlet, useParams } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider } from '@/context/AuthContext'
import { useAuth } from '@/hooks/useAuth'
import { DashboardProvider } from '@/context/DashboardContext'
import { AppShell } from '@/components/layout/AppShell'
import { DashboardPage } from '@/components/dashboard/DashboardPage'
import { OffersIndexPage } from '@/components/dashboard/OffersIndexPage'
import { LoginPage } from '@/components/auth/LoginPage'
import { Spinner } from '@/components/shared/Spinner'
import type { ReactNode } from 'react'

const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'))

const GeoAllocationsPage = lazy(() => import('@/components/admin/GeoAllocationsPage').then(m => ({ default: m.GeoAllocationsPage })))
const OfferCapsPage = lazy(() => import('@/components/admin/OfferCapsPage').then(m => ({ default: m.OfferCapsPage })))
const UsersPage = lazy(() => import('@/components/admin/UsersPage').then(m => ({ default: m.UsersPage })))
const CapRecoveryPage = lazy(() => import('@/components/admin/CapRecoveryPage').then(m => ({ default: m.CapRecoveryPage })))
const AuditLogPage = lazy(() => import('@/components/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const SyncStatusPage = lazy(() => import('@/components/admin/SyncStatusPage').then(m => ({ default: m.SyncStatusPage })))
const DocsPage = lazy(() => import('@/components/admin/DocsPage').then(m => ({ default: m.DocsPage })))
const ImpersonateDashboardPage = lazy(() => import('@/components/admin/ImpersonateDashboardPage').then(m => ({ default: m.ImpersonateDashboardPage })))
const CapRequestsPage = lazy(() => import('@/components/admin/CapRequestsPage').then(m => ({ default: m.CapRequestsPage })))
const AccessRequestsPage = lazy(() => import('@/components/admin/AccessRequestsPage').then(m => ({ default: m.AccessRequestsPage })))
const SpendBreakdownPage = lazy(() => import('@/components/admin/SpendBreakdownPage').then(m => ({ default: m.SpendBreakdownPage })))
const LeakagePage = lazy(() => import('@/components/admin/LeakagePage').then(m => ({ default: m.LeakagePage })))
const TrendsPage = lazy(() => import('@/components/admin/TrendsPage').then(m => ({ default: m.TrendsPage })))
const SharedDashboardPage = lazy(() => import('@/components/dashboard/SharedDashboardPage').then(m => ({ default: m.SharedDashboardPage })))
const PortalPage = lazy(() => import('@/components/portal/PortalPage').then(m => ({ default: m.PortalPage })))
const PortalResetPage = lazy(() => import('@/components/portal/PortalResetPage').then(m => ({ default: m.PortalResetPage })))

const pageFallback = <div className="flex items-center justify-center py-20"><Spinner className="w-8 h-8" /></div>

function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  if (!isAuthenticated) return <LoginPage />

  return <>{children}</>
}

/**
 * `/offer/:offerId` — one offer's geos and caps. The provider is seeded with the offer in the
 * URL so the first fetch is already scoped to it, and remounting on a different id (via `key`)
 * throws away the previous offer's allocations instead of showing them under the new name.
 */
function OfferDashboardRoute() {
  const { offerId } = useParams<{ offerId: string }>()
  const id = Number(offerId)
  const initialOfferId = Number.isFinite(id) && id > 0 ? id : null
  return (
    <DashboardProvider key={initialOfferId ?? 'default'} initialOfferId={initialOfferId}>
      <DashboardPage />
    </DashboardProvider>
  )
}

/**
 * `/` — the offer list for affiliates and owners. General users have no per-offer allocations
 * (they run one shared pool), so they keep landing straight on their own dashboard.
 */
function HomeRoute() {
  const { user } = useAuth()
  if (user?.user_type === 'general') {
    return (
      <DashboardProvider>
        <DashboardPage />
      </DashboardProvider>
    )
  }
  return <OffersIndexPage />
}

/** Authenticated app shell — wraps the logged-in routes via an Outlet. */
function AuthedShell() {
  return (
    <AuthProvider>
      <AuthGate>
        <AppShell>
          <Suspense fallback={pageFallback}>
            <Outlet />
          </Suspense>
        </AppShell>
      </AuthGate>
    </AuthProvider>
  )
}

const routerBasename = (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || undefined

export default function App() {
  return (
    <BrowserRouter basename={routerBasename}>
      <Routes>
        {/* Public global affiliate portal (enter Everflow ID → dashboard) */}
        <Route path="/portal" element={<Suspense fallback={pageFallback}><PortalPage /></Suspense>} />
        <Route path="/portal/reset/:token" element={<Suspense fallback={pageFallback}><PortalResetPage /></Suspense>} />

        {/* Public, no-login share link */}
        <Route path="/v/:token" element={<Suspense fallback={pageFallback}><SharedDashboardPage /></Suspense>} />

        {/* Authenticated app */}
        <Route element={<AuthedShell />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/offer/:offerId" element={<OfferDashboardRoute />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<GeoAllocationsPage />} />
            <Route path="offer-caps" element={<OfferCapsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId/dashboard" element={<ImpersonateDashboardPage />} />
            <Route path="requests" element={<CapRequestsPage />} />
            <Route path="access-requests" element={<AccessRequestsPage />} />
            <Route path="recovery" element={<CapRecoveryPage />} />
            <Route path="spend" element={<SpendBreakdownPage />} />
            <Route path="leakage" element={<LeakagePage />} />
            <Route path="trends" element={<TrendsPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="sync" element={<SyncStatusPage />} />
            <Route path="docs" element={<DocsPage />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
